// MUST stay first: bootClean decides (synchronously) whether this load needs a
// storage wipe, before any provider reads persisted state.
import bootCleanMode from "./utils/bootClean";
// Dev-only (localhost): keeps Privy on auth.privy.io so sessions survive, see the module header.
import "./utils/privyLocalDev";
import React from "react";
import { createRoot } from 'react-dom/client';
import App from "./App";
import "./index.css";
import { BrowserRouter } from "react-router-dom";
//TODOGZ reenable
import { ToastContainer } from "react-toastify"; // Import ToastContainer and toast
import store from "./store";
import Provider from "react-redux/es/components/Provider";
import 'react-toastify/dist/ReactToastify.css';
import WalletProvider from "./components/WalletProvider";
import { DASHBOARD_VERSION } from "./utils/appVersion";
import { purgeBrowserStores, checkForNewVersion } from "./utils/cacheReset";

// bootClean (imported first, above) has already done the synchronous half of
// whatever this load needs: a full wipe on ?reset=1 or a version bump, or the
// everyday "logged out, so drop any leftover wallet state" clean. Finish the
// asynchronous half here.
//
// The everyday clean is the one that matters. A hard reload (Ctrl+Shift+R)
// re-downloads the bundle but leaves localStorage, cookies and IndexedDB in
// place — and it is that leftover state, at the SAME app version, that wedges
// the login. Every logged-out page load now starts from a clean slate on its
// own, with no version bump and no reload needed.
if (bootCleanMode !== "none") {
  purgeBrowserStores({ walletOnly: bootCleanMode === "walletOnly" }).then(() => {
    if (bootCleanMode === "forced") {
      // ?reset=1 — reload once onto a clean origin, without the flag.
      const url = new URL(window.location.href);
      url.searchParams.delete("reset");
      url.searchParams.set("_cb", Date.now().toString(36));
      window.location.replace(url.toString());
    }
  });
}

// Belt and braces: ask the server which version is actually deployed, so a
// browser stuck on an old bundle (which by definition cannot know it is old)
// wipes and reloads once.
checkForNewVersion(DASHBOARD_VERSION);

// Wallet connection attempts that the user cancels (or that MetaMask aborts)
// surface as unhandled promise rejections from the extension's inpage script,
// sometimes as plain objects instead of Errors. AppKit already shows the
// failure state in its own UI, so swallow these to avoid duplicate noise.
window.addEventListener('unhandledrejection', (event) => {
  let msg = '';
  try {
    msg =
      (event?.reason?.message || '') +
      ' ' +
      JSON.stringify(event?.reason, Object.getOwnPropertyNames(Object(event?.reason ?? {})));
  } catch {
    msg = String(event?.reason || '');
  }
  if (
    /Failed to connect|User rejected|user rejected|Connection request reset|Connector not connected|ConnectorNotFound|Provider not found|ProviderNotFound|MetaMask|metamask|\b4001\b|-32002/i.test(
      msg
    )
  ) {
    console.warn('[Wallet] Connection attempt failed or was rejected:', event.reason);
    event.preventDefault();
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <WalletProvider>
      <BrowserRouter>
        <App />
        {/* ToastContainer is a required component for react-toastify */}
        <ToastContainer
          position="top-center" // Example position; change as needed
          autoClose={10000} // Example timeout duration; change as needed
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored" // Example theme; change as needed
        />
      </BrowserRouter>
    </WalletProvider>
  </Provider>,
);

// Always unregister any legacy service worker. The old cache-first worker
// served stale app shell/JS after deploys; the current sw.js is a
// self-destructing kill-switch. Cache API purging is tied to a version bump
// above (a new deploy), not to every page load.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (let registration of registrations) {
      registration.unregister();
    }
  });
}

let deferredPrompt;
window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent the default mini-infobar from appearing
  e.preventDefault();
  deferredPrompt = e;

  // Only surface the PWA install prompt AFTER login, never on the auth pages.
  const loggedIn = !!window.localStorage.getItem('hash');
  const onAuthPage = /^\/(login|admin-login)/.test(window.location.pathname);
  if (!window.localStorage.getItem('pwaPromptShown') && loggedIn && !onAuthPage) {
    // Create a custom snackbar for the install prompt
    const snackbar = document.createElement('div');
    snackbar.id = "pwaSnackbar";
    snackbar.style.position = "fixed";
    snackbar.style.left = "50%";
    snackbar.style.bottom = "20px";
    snackbar.style.transform = "translateX(-50%)";
    snackbar.style.backgroundColor = "#151617";
    snackbar.style.color = "#e9efeb";
    snackbar.style.padding = "16px";
    snackbar.style.borderRadius = "12px";
    snackbar.style.border = "1px solid rgba(255,255,255,0.1)";
    snackbar.style.fontFamily = "'Manrope', sans-serif";
    snackbar.style.boxShadow = "0 20px 50px -20px rgba(0,0,0,0.8)";
    snackbar.style.display = "flex";
    snackbar.style.alignItems = "center";
    snackbar.style.zIndex = "1000";

    // Message text
    const message = document.createElement('span');
    message.innerText = "Install this app for a better experience.";
    snackbar.appendChild(message);

    // Spacer between message and buttons
    const spacer = document.createElement('div');
    spacer.style.flex = "1";
    snackbar.appendChild(spacer);

    // INSTALL action button with updated styling
    const actionButton = document.createElement('button');
    actionButton.innerText = "INSTALL";
    actionButton.style.background = "#3ad17e";
    actionButton.style.border = "none";
    actionButton.style.color = "#04130b";
    actionButton.style.fontWeight = "bold";
    actionButton.style.cursor = "pointer";
    actionButton.style.padding = "10px 14px";
    actionButton.style.borderRadius = "9px";
    actionButton.style.marginLeft = "20px";
    snackbar.appendChild(actionButton);

    // DISMISS button with similar styling adjustments
    const dismissButton = document.createElement('button');
    dismissButton.innerText = "DISMISS";
    dismissButton.style.background = "transparent";
    dismissButton.style.border = "none";
    dismissButton.style.color = "white";
    dismissButton.style.fontWeight = "bold";
    dismissButton.style.cursor = "pointer";
    dismissButton.style.padding = "10px 5px 10px 5px";
    dismissButton.style.borderRadius = "8px";
    dismissButton.style.marginLeft = "10px";
    snackbar.appendChild(dismissButton);

    document.body.appendChild(snackbar);

    actionButton.addEventListener('click', () => {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        console.log(`User response to the install prompt: ${choiceResult.outcome}`);
        window.localStorage.setItem('pwaPromptShown', 'true');
        if (snackbar.parentNode) {
          snackbar.parentNode.removeChild(snackbar);
        }
        deferredPrompt = null;
      });
    });

    dismissButton.addEventListener('click', () => {
      window.localStorage.setItem('pwaPromptShown', 'true');
      if (snackbar.parentNode) {
        snackbar.parentNode.removeChild(snackbar);
      }
      deferredPrompt = null;
    });
  }
});
